<template>
  <v-dialog v-model="dialog" :retain-focus="false" persistent :fullscreen="isMobile ? true : false" max-width="1100px">
    <v-card :loading="isLoadingSaveProlong">
      <v-card-title :class="isMobile ? 'pt-10' : 'headline'">
        <v-row>
          <v-col cols="12" class="d-flex align-center">
            <span class="headline">{{ $t("bookings.prolong_short") }}</span>
            <v-spacer></v-spacer>
            <v-btn icon class="ml-2 green--text" @click="save()" :loading="isLoadingSaveProlong">
              <v-icon>mdi-content-save-all-outline</v-icon>
            </v-btn>
            <v-btn icon v-if="user_role != 'guest' && user_role != 'partner'" color="warning"
              @click="generateProlongDoc(booking)" :loading="isLoadingGenerateProlongDoc" v-tooltip="{
                content: $t('bookings.generate_docs'),
                placement: 'bottom-center',
                classes: ['info'],
                targetClasses: ['it-has-a-tooltip'],
                delay: {
                  show: 500,
                  hide: 500,
                },
              }">
              <v-icon>mdi-file-document-outline</v-icon>
            </v-btn>
            <v-icon :class="$vuetify.theme.dark ? 'ml-2 white--text' : 'ml-2 black--text'" @click="close()">
              mdi-close
            </v-icon>
          </v-col>
          <v-col cols="12">
            <v-divider></v-divider>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-simple-table>
              <tbody>
                <tr>
                  <th>{{ $t("bookings.booking") }} {{ $t("custom.number") }}</th>
                  <td>{{ booking.id }}</td>
                </tr>
                <tr>
                  <th>{{ $t("bookings.client") }}</th>
                  <td>
                    <router-link :to="{ path: `/clients/${booking.client_id}` }">
                      {{ booking.last_name }} {{ booking.first_name }} {{ booking.middle_name }}
                    </router-link>
                  </td>
                </tr>
                <tr>
                  <th>{{ $t("bookings.booking_end") }}</th>
                  <td>{{ booking.end_date }}</td>
                </tr>
                <tr :class="$vuetify.theme.dark ? 'text-overline grey darken-1' : 'text-overline grey lighten-1'">
                  <th :class="isMobile ? 'text-center' : ''" @click="copyToBuffer(booking.selected_price)"
                    style="cursor: pointer;">{{ $t("bookings.current_price") }}</th>
                  <td :class="isMobile ? 'text-center' : ''" @click="copyToBuffer(booking.selected_price)"
                    style="cursor: pointer;">{{ booking.selected_price }}{{ findCurrencySymbol(base_currency_id) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-col>
          <v-col cols="12">
            <v-divider></v-divider>
          </v-col>
          <v-col v-if="!booking.hourly && !booking.weekly && !booking.monthly" cols="12">
            <v-row>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.days" :label="$t('prolongs.for_days')" type="number" :min="1"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.price" :label="$t('prolongs.for_price')" type="number"
                  style="cursor: pointer;" :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col v-if="booking.hourly" cols="12">
            <v-row>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.hourly_hours" :label="$t('prolongs.for_hours')" type="number"
                  :min="1" :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.price" :label="$t('prolongs.for_price')" type="number"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col v-if="booking.weekly" cols="12">
            <v-row>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.weekly_weeks" :label="$t('prolongs.for_weeks')" type="number"
                  :min="1" :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.price" :label="$t('prolongs.for_price')" type="number"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col v-if="booking.monthly" cols="12">
            <v-row>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.monthly_months" :label="$t('prolongs.for_months')"
                  type="number" :min="1" :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
              <v-col cols="12" md="6" sm="12">
                <v-text-field v-model.number="prolongData.price" :label="$t('prolongs.for_price')" type="number"
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
            </v-row>
          </v-col>
          <v-col v-if="!booking.hourly && !booking.weekly && !booking.monthly" cols="12">
            <v-alert :value="true" color="info" dense class="mb-0 text-overline text-center" outlined>
              <span class="text-overline font-weight-bold">{{ $t("prolongs.total_rent") }}:</span>
              <span class="text-overline font-weight-bold ml-2 warning--text"
                v-if="prolongData.currency_id && prolongData.currency_id != base_currency_id && prolongData.days && prolongData.days != 0"
                @click="copyToBuffer(sumInNonBaseCurrency(prolongData.days * prolongData.price, prolongData.currency_id))"
                style="cursor: pointer;">{{ messageInNonBaseCurrency(prolongData.days * prolongData.price,
                  prolongData.currency_id) }}</span>
              <span class="text-overline font-weight-bold ml-2">{{ parseFloat((prolongData.days *
                prolongData.price).toFixed(2)) }}{{ findCurrencySymbol(base_currency_id) }}</span>
            </v-alert>
          </v-col>
          <v-col v-if="booking.hourly" cols="12">
            <v-alert :value="true" color="info" dense class="mb-0 text-overline text-center" outlined>
              <span class="text-overline font-weight-bold">{{ $t("prolongs.total_rent") }}:</span>
              <span class="text-overline font-weight-bold ml-2 warning--text"
                v-if="prolongData.currency_id && prolongData.currency_id != base_currency_id && prolongData.hourly_hours && prolongData.hourly_hours != 0"
                @click="copyToBuffer(sumInNonBaseCurrency(prolongData.hourly_hours * prolongData.price, prolongData.currency_id))"
                style="cursor: pointer;">{{ messageInNonBaseCurrency(prolongData.hourly_hours * prolongData.price,
                  prolongData.currency_id) }}</span>
              <span class="text-overline font-weight-bold ml-2">{{ parseFloat((prolongData.hourly_hours *
                prolongData.price).toFixed(2)) }}{{ findCurrencySymbol(base_currency_id) }}</span>
            </v-alert>
          </v-col>
          <v-col v-if="booking.weekly" cols="12">
            <v-alert :value="true" color="info" dense class="mb-0 text-overline text-center" outlined>
              <span class="text-overline font-weight-bold">{{ $t("prolongs.total_rent") }}:</span>
              <span class="text-overline font-weight-bold ml-2 warning--text"
                v-if="prolongData.currency_id && prolongData.currency_id != base_currency_id && prolongData.weekly_weeks && prolongData.weekly_weeks != 0"
                @click="copyToBuffer(sumInNonBaseCurrency(prolongData.weekly_weeks * prolongData.price, prolongData.currency_id))"
                style="cursor: pointer;">{{ messageInNonBaseCurrency(prolongData.weekly_weeks * prolongData.price,
                  prolongData.currency_id) }}</span>
              <span class="text-overline font-weight-bold ml-2">{{ parseFloat((prolongData.weekly_weeks *
                prolongData.price).toFixed(2)) }}{{ findCurrencySymbol(base_currency_id) }}</span>
            </v-alert>
          </v-col>
          <v-col v-if="booking.monthly" cols="12">
            <v-alert :value="true" color="info" dense class="mb-0 text-overline text-center" outlined>
              <span class="text-overline font-weight-bold">{{ $t("prolongs.total_rent") }}:</span>
              <span class="text-overline font-weight-bold ml-2 warning--text"
                v-if="prolongData.currency_id && prolongData.currency_id != base_currency_id && prolongData.monthly_months && prolongData.monthly_months != 0"
                @click="copyToBuffer(sumInNonBaseCurrency(prolongData.monthly_months * prolongData.price, prolongData.currency_id))"
                style="cursor: pointer;">{{ messageInNonBaseCurrency(prolongData.monthly_months * prolongData.price,
                  prolongData.currency_id) }}</span>
              <span class="text-overline font-weight-bold ml-2">{{ parseFloat((prolongData.monthly_months *
                prolongData.price).toFixed(2)) }}{{ findCurrencySymbol(base_currency_id) }}</span>
            </v-alert>
          </v-col>
          <!-- Аренда -->
          <v-col cols="12">
            <v-row align="center">
              <v-col auto>
                <v-text-field v-model.number="prolongData.sum" :label="$t('prolongs.get_for_rent')"
                  :messages="messageInBaseCurrency(prolongData.sum, prolongData.currency_id)" dense
                  :disabled="isLoadingSaveProlong"></v-text-field>
              </v-col>
              <v-col auto v-if="company && company.multicurrency_cashboxes">
                <v-select v-model="prolongData.company_cashbox" :items="active_company_cashboxes"
                  :item-text="item => item.name" :return-object="true"
                  @change="prolongData.currency_id = prolongData.company_cashbox.currency_id, prolongData.company_cashbox_id = prolongData.company_cashbox.id"
                  :label="$t('companies.currency')" dense :disabled="isLoadingSaveProlong"></v-select>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12">
            <v-row align="center">
              <v-col cols="12" class="d-flex align-center justify-space-between">
                <v-checkbox v-if="company && company.cash_payment" v-model="prolongData.type" color="success"
                  value="Наличные" :disabled="prolongData.type == 'Терминал' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    " class="mt-0 mr-0" dense hide-details :style="prolongData.type == 'Терминал' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
                <v-checkbox v-if="company && company.cashless_payment" v-model="prolongData.type" color="warning"
                  value="Терминал" :disabled="prolongData.type == 'Наличные' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    " class="mt-0 mr-0" dense hide-details :style="prolongData.type == 'Наличные' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
                <v-checkbox v-if="company && company.card_to_card_payment" v-model="prolongData.type" color="info"
                  value="На карту" :disabled="prolongData.type == 'Наличные' ||
                    prolongData.type == 'Терминал' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    " class="mt-0 mr-0" dense hide-details :style="prolongData.type == 'Наличные' ||
                    prolongData.type == 'Терминал' ||
                    prolongData.type == 'Р/с' ||
                    prolongData.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
                <v-checkbox v-if="company && company.entity_payment" v-model="prolongData.type" color="secondary"
                  class="mt-0 mr-0" dense hide-details value="Р/с" :disabled="prolongData.type == 'Наличные' ||
                    prolongData.type == 'Терминал' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Баланс клиента'
                    " :style="prolongData.type == 'Наличные' ||
                      prolongData.type == 'Терминал' ||
                      prolongData.type == 'На карту' ||
                      prolongData.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                        content: $t('tooltips.entity_count'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 500,
                          hide: 500,
                        },
                      }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
                <v-checkbox v-if="company && company.client_payment" v-model="prolongData.type" color="error"
                  class="mt-0 mr-0" dense hide-details value="Баланс клиента" :disabled="prolongData.type == 'Наличные' ||
                    prolongData.type == 'Терминал' ||
                    prolongData.type == 'На карту' ||
                    prolongData.type == 'Р/с'
                    " :style="prolongData.type == 'Наличные' ||
                      prolongData.type == 'Терминал' ||
                      prolongData.type == 'На карту' ||
                      prolongData.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                        content: $t('money.client_balance'),
                        placement: 'bottom-center',
                        classes: ['info'],
                        targetClasses: ['it-has-a-tooltip'],
                        delay: {
                          show: 500,
                          hide: 500,
                        },
                      }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close()">{{ $t("custom.close") }}</v-btn>
        <v-btn color="success" @click="save()" :loading="isLoadingSaveProlong">{{ $t("custom.save") }}</v-btn>
        <v-spacer></v-spacer>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import "moment/locale/ru";
import "moment/locale/en-gb";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import PizZip from "pizzip";
import { saveAs } from "file-saver";
import Docxtemplater from "docxtemplater";
export default {
  name: 'ProlongDialog',
  data() {
    return {
      prolongData: {
        days: 0,
        hourly_hours: 0,
        monthly_months: 0,
        weekly_weeks: 0,
        price: 0,
        additional_hours: 0,
        cash: 0,
        cashless: 0,
        cash_card: 0,
        entity: 0,
        client: 0,
        currency_id: null,
        company_cashbox_id: null,
        company_cashbox: null,
      },
      paymentsPanel: [0],
      isLoadingGenerateProlongDoc: false,
      error: null,
    }
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    booking: {
      type: Object,
      required: true
    },
    client: {
      type: Object,
      required: true
    },
    set_car: {
      type: Object,
      required: true
    },
    add_drivers: {
      type: Array,
      default: () => []
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    currency: {
      type: String,
      default: ''
    },
    user_role: {
      type: String,
      default: ''
    },
    company: {
      type: Object,
      default: null
    },
    company_cashboxes: {
      type: Array,
      default: () => []
    },
    worker_accounts: {
      type: Array,
      default: () => []
    },
    showSelectWorkerCashbox: {
      type: Boolean,
      default: false
    },
    company_cashbox_id: {
      type: [Number, String],
      default: null
    },
    isLoadingSaveProlong: {
      type: Boolean,
      default: false
    },
  },
  mounted() {
    if (this.base_currency_id && this.active_company_cashboxes && this.active_company_cashboxes.length > 0) {
      this.prolongData.currency_id = this.base_currency_id;
      this.prolongData.company_cashbox = this.active_company_cashboxes.find((cashbox) => cashbox.is_default);
      this.prolongData.company_cashbox_id = this.prolongData.company_cashbox.id;
    }
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    isNative() {
      return Capacitor.isNativePlatform();
    },
    active_company_cashboxes() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company && this.$store.getters.getCompany.company.company_cashboxes) {
        let cashboxes = [];
        this.$store.getters.getCompany.company.company_cashboxes.forEach((cashbox) => {
          if (cashbox.id && cashbox.is_active) {
            cashboxes.push(cashbox);
          }
        });
        cashboxes.sort((a, b) => {
          // сортировка по is_default
          if (a.is_default && !b.is_default) {
            return -1;
          }
        });
        return cashboxes;
      } else {
        return [];
      }
    },
    currencies() {
      if (
        this.$store.getters.getCompany &&
        this.$store.getters.getCompany.company &&
        this.$store.getters.getCompany.company.currencies
      ) {
        return this.$store.getters.getCompany.company.currencies;
      } else {
        return [];
      }
    },
    base_currency_id() {
      if (this.active_company_cashboxes && this.active_company_cashboxes.length > 0) {
        return this.active_company_cashboxes.find((cashbox) => cashbox.is_default).currency_id;
      } else {
        return null;
      }
    },
    exchange_rates() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company && this.$store.getters.getCompany.company.exchange_rates) {
        return this.$store.getters.getCompany.company.exchange_rates;
      } else {
        return [];
      }
    },
  },
  methods: {
    save() {
      if (this.prolongData.currency_id != this.base_currency_id && this.prolongData.type == "Баланс клиента") {
        this.$notify(this.$t("errors.client_payments_in_non_base_currency"), "", "error", 4000);
        return;
      }
      this.$emit('save', this.prolongData);
    },
    close() {
      this.$emit('close');
    },
    sumInBaseCurrency(sum, currency_id) {
      if (this.exchange_rates.length > 0 && currency_id) {
        let exchange_rate = this.exchange_rates.find((exchange_rate) => exchange_rate.target_currency_id == currency_id);
        if (exchange_rate) {
          return (parseFloat(sum) / parseFloat(exchange_rate.rate)).toFixed(2);
        } else {
          return sum / 1;
        }
      } else {
        return sum / 1;
      }
    },
    sumInNonBaseCurrency(sum, currency_id) {
      if (this.exchange_rates.length > 0 && currency_id) {
        let exchange_rate = this.exchange_rates.find((exchange_rate) => exchange_rate.target_currency_id == currency_id);
        if (exchange_rate) {
          return (parseFloat(sum) * parseFloat(exchange_rate.rate)).toFixed(2);
        } else {
          return sum / 1;
        }
      } else {
        return sum / 1;
      }
    },
    messageInBaseCurrency(sum, currency_id) {
      if (sum && currency_id && this.base_currency_id && this.base_currency_id != currency_id) {
        return `${this.sumInBaseCurrency(sum, currency_id)} ${this.findCurrencySymbol(this.base_currency_id)}`;
      } else {
        return '';
      }
    },
    messageInNonBaseCurrency(sum, currency_id) {
      if (sum && currency_id && this.base_currency_id && this.base_currency_id != currency_id) {
        return `${this.sumInNonBaseCurrency(sum, currency_id)} ${this.findCurrencySymbol(currency_id)}`;
      } else {
        return '';
      }
    },
    findCurrencySymbol(id) {
      if (id != null) {
        if (this.currencies.find((currency) => currency.id == id)) {
          return this.currencies.find((currency) => currency.id == id).symbol;
        }
      }
    },
    generateProlongDoc(booking) {
      if (this.user_role != "guest" && this.user_role != "partner") {
        this.isLoadingGenerateProlongDoc = true;
        let self = this;
        let prolong_data = this.prolongData;
        this.axios
          .get(`api/v1/prolong_templates`, {
            headers: {
              Authorization: this.$store.getters.getAuthToken,
            },
          })
          .then((response) => {
            let content, error, url;
            if (response.data && response.data.url) {
              url = response.data.url;
            } else {
              if (self.$i18n.locale == "ru") {
                url = "https://web.rentprog.ru/prolong_example.docx";
              } else {
                url = "https://web.rentprog.com/prolong_example_en.docx";
              }
            }
            this.axios
              .get(url, {
                responseType: "arraybuffer",
                headers: {
                  "Content-Type": "text/plain;charset=utf-8",
                },
              })
              .then((response) => {
                content = response.data;
                var zip = new PizZip(content);
                let client = self.client;
                let car = self.set_car;
                let add_drivers_info = "";
                if (self.add_drivers && self.add_drivers.length > 0) {
                  self.add_drivers.forEach((driver) => {
                    add_drivers_info += `${driver.lastname} ${driver.name} ${driver.middlename
                      }, ${self.$t("custom.passport")}: ${driver.passport_series
                      } ${driver.passport_number}, ${self.$t(
                        "custom.driver_license"
                      )}: ${driver.driver_series} ${driver.driver_number}${self.add_drivers.length > 1 ? ", " : ""
                      }`;
                  });
                }
                // Считаю предоплату
                let prepaid = 0;
                if (booking.counts && booking.counts.length > 0) {
                  booking.counts.forEach((count) => {
                    if (count.completed) {
                      if (this.company.multicurrency_cashboxes && this.company.multicurrency_cashboxes == true && count.rated_sum != null) {
                        let ratedSum = Number(count.rated_sum);
                        prepaid += ratedSum;
                      } else {
                        let sum = Number(count.sum);
                        prepaid += sum;
                      }
                    }
                  });
                  // Округляем сумму до 2 знаков после запятой
                  prepaid = parseFloat(prepaid.toFixed(2));
                }
                // Считаю доп сервисы
                let additional_services =
                  (booking.hours_cost > 0
                    ? booking.additional_hours * booking.price_hour
                    : 0) +
                  booking.hours_cost_end +
                  booking.delivery +
                  booking.delivery_end +
                  booking.equipment +
                  booking.clean_payment +
                  booking.damage +
                  booking.gas +
                  booking.add_drivers_cost +
                  booking.insurance +
                  booking.fine +
                  booking.other +
                  booking.other_end;

                // Считаю комиссию в сутки
                let commission = booking.aggr_commission;
                let commission_day = commission / (booking.days > 0 ? booking.days : 1);

                var doc = new Docxtemplater().loadZip(zip);
                doc.setData({
                  prolong_sum: prolong_data.price * prolong_data.days,
                  prolong_price: prolong_data.price,
                  prolong_days: prolong_data.days,
                  prolong_end_date: moment(booking.end_date, "DD-MM-YYYY H:mm")
                    .add(prolong_data.days, "days")
                    .format("DD.MM.YYYY"),
                  date: moment().format("DD/MM/YY"),
                  date_short: moment().format("DD/MM"),
                  serial_number: booking.serial_number ? booking.serial_number : "      ",
                  employee_full_name: self.user_full_name,
                  employee_short_name: self.user_fio,
                  first_name: client.name ? client.name : "      ",
                  middle_name: client.middlename ? client.middlename : "      ",
                  last_name: client.lastname ? client.lastname : "      ",
                  short_name: client.fio ? client.fio : "      ",
                  client_id: client.id ? client.id : "      ",
                  booking_id: booking.id ? booking.id : "      ",
                  car_id: car.id ? car.id : "      ",
                  date_birth: client.birthday
                    ? moment(client.birthday, "YYYY-MM-DD").format("DD-MM-YYYY")
                    : "      ",
                  passport_series: client.passport_series
                    ? client.passport_series
                    : "      ",
                  passport_number: client.passport_number
                    ? client.passport_number
                    : "      ",
                  passport_issued: client.passport_issued
                    ? client.passport_issued
                    : "      ",
                  driver_series: client.driver_series
                    ? client.driver_series
                    : "      ",
                  driver_number: client.driver_number
                    ? client.driver_number
                    : "      ",
                  driver_issued: client.driver_issued
                    ? client.driver_issued
                    : "      ",
                  taxi_license: client.taxi_license
                    ? client.taxi_license
                    : "      ",
                  inn: client.inn ? client.inn : "      ",
                  country: client.country ? client.country : "      ",
                  city: client.city ? client.city : "      ",
                  address: client.address ? client.address : "      ",
                  phone: client.phone ? client.phone : "      ",
                  email: client.email ? client.email : "      ",
                  entity_adress: client.entity_adress
                    ? client.entity_adress
                    : "     ",
                  entity_name: client.entity_name
                    ? client.entity_name
                    : "     ",
                  ceo: client.ceo ? client.ceo : "     ",
                  reg_form: client.reg_form ? client.reg_form : "     ",
                  entity_phone: client.entity_phone
                    ? client.entity_phone
                    : "     ",
                  ogrn: client.ogrn ? client.ogrn : "     ",
                  acc_number: client.acc_number ? client.acc_number : "     ",
                  bank: client.bank ? client.bank : "     ",
                  korr: client.korr ? client.korr : "     ",
                  bik: client.bik ? client.bik : "     ",
                  email: client.email ? client.email : "     ",
                  doc_number: client.doc_number ? client.doc_number : "     ",
                  start_date: booking.start_date
                    ? booking.start_date
                    : "      ",
                  end_date: booking.end_date ? booking.end_date : "      ",
                  start_date_short: booking.start_date
                    ? moment(booking.start_date, "DD-MM-YYYY H:mm").format(
                      "DD.MM.YYYY"
                    )
                    : "     ",
                  end_date_short: booking.end_date
                    ? moment(booking.end_date, "DD-MM-YYYY H:mm").format(
                      "DD.MM.YYYY"
                    )
                    : "     ",
                  day_cost: car.prices.find((price) => price.season_id === null)
                    ? car.prices.find((price) => price.season_id === null)
                      .values[0]
                    : booking.start_price,
                  price: booking.price ? booking.price : "      ",
                  day_cost_without_commission: car.prices && car.prices.find((price) => price.season_id === null)
                    ? (car.prices.find((price) => price.season_id === null)
                      .values[0] - commission_day).toFixed(2)
                    : booking.start_price,
                  price_without_commission: booking.price
                    ? (booking.price - commission_day).toFixed(2)
                    : "      ",
                  cost_without_commission: booking.rental_cost
                    ? (booking.rental_cost - commission).toFixed(2)
                    : "      ",
                  selected_price: booking.selected_price
                    ? booking.selected_price
                    : "      ",
                  cost: booking.rental_cost ? booking.rental_cost : "      ",
                  days: booking.days ? booking.days : "      ",
                  location_start: booking.location_start
                    ? booking.location_start
                    : "      ",
                  location_end: booking.location_end
                    ? booking.location_end
                    : "      ",
                  delivery_start: booking.delivery
                    ? booking.delivery
                    : "      ",
                  delivery_end: booking.delivery_end
                    ? booking.delivery_end
                    : "      ",
                  equipment: booking.equipment ? booking.equipment : "      ",
                  hours_cost: booking.hours_cost
                    ? booking.hours_cost
                    : "      ",
                  additional_hours: booking.additional_hours
                    ? booking.additional_hours
                    : "      ",
                  add_drivers_cost: booking.add_drivers_cost
                    ? booking.add_drivers_cost
                    : "      ",
                  insurance: booking.insurance ? booking.insurance : "      ",
                  clean: booking.clean_payment
                    ? booking.clean_payment
                    : "      ",
                  other: booking.other ? booking.other : "      ",
                  mileage_limit: car.extra_mileage_km ? car.extra_mileage_km : this.company.mileage_limit || 0,
                  extra_mileage_km: car.extra_mileage_price ? car.extra_mileage_price : this.company.mileage_limit_cost || 0,
                  has_delivery_start: booking.delivery ? true : false,
                  has_delivery_end: booking.delivery_end ? true : false,
                  has_equipment: booking.equipment ? true : false,
                  has_additional_hours: booking.additional_hours ? true : false,
                  has_hours_cost: booking.hours_cost ? true : false,
                  has_add_drivers_cost: booking.add_drivers_cost ? true : false,
                  has_insurance: booking.insurance ? true : false,
                  has_clean: booking.clean_payment ? true : false,
                  has_other: booking.other ? true : false,
                  has_sale: booking.sale ? true : false,
                  has_sale_cash: booking.sale_cash ? true : false,
                  sale: booking.sale ? booking.sale : "",
                  sale_cash: booking.sale_cash ? booking.sale_cash : "",
                  start_mileage: booking.start_mileage
                    ? booking.start_mileage
                    : "      ",
                  end_mileage: booking.end_mileage
                    ? booking.end_mileage
                    : "      ",
                  car_name: booking.car_name ? booking.car_name : "      ",
                  car_code: booking.car_code ? booking.car_code : "      ",
                  car_number: car.number ? car.number : "      ",
                  car_vin: car.vin ? car.vin : "      ",
                  car_body: car.body_number ? car.body_number : "      ",
                  car_pts: car.pts ? car.pts : "      ",
                  car_purchase_price: car.purchase_price ? car.purchase_price : "      ",
                  car_reg: car.registration_certificate
                    ? car.registration_certificate
                    : "      ",
                  car_color: car.color ? car.color : "      ",
                  car_year: car.year ? car.year : "      ",
                  car_fuel: car.fuel ? car.fuel : "      ",
                  price_hour: car.price_hour ? car.price_hour : "      ",
                  additional_services:
                    additional_services > 0 ? additional_services : "      ",
                  has_additional_services:
                    additional_services > 0 ? true : false,
                  deposit: this.$emit('current-deposit') ? this.$emit('current-deposit') : "      ",
                  total: booking.total ? booking.total : "      ",
                  total_without_commission:
                    booking.total
                      ? booking.total - commission
                      : "      ",
                  total_with_deposit:
                    booking.total && this.$emit('current-deposit')
                      ? booking.total + this.$emit('current-deposit')
                      : "      ",
                  total_with_deposit_without_commission:
                    booking.total && this.$emit('current-deposit')
                      ? booking.total + this.$emit('current-deposit') - commission
                      : "      ",
                  prepaid: prepaid,
                  add_drivers_info: add_drivers_info,
                  repair_cost: car.repair_cost ? car.repair_cost : "      ",
                  franchise: car.franchise ? car.franchise : "      ",
                  max_fine: car.max_fine ? car.max_fine : "      ",
                  custom_field_1: booking.custom_field_1
                    ? booking.custom_field_1
                    : "      ",
                  custom_field_2: booking.custom_field_2
                    ? booking.custom_field_2
                    : "      ",
                  custom_field_3: booking.custom_field_3
                    ? booking.custom_field_3
                    : "      ",
                  cars_custom_field_1: car.custom_field_1
                    ? car.custom_field_1
                    : "      ",
                  cars_custom_field_2: car.custom_field_2
                    ? car.custom_field_2
                    : "      ",
                  cars_custom_field_3: car.custom_field_3
                    ? car.custom_field_3
                    : "      ",
                });

                doc.render();

                var out = doc.getZip().generate({
                  type: "blob",
                  mimeType:
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                }); //Output the document using Data-URI
                let date = moment().format("Y_MM_DD");
                // console.log(client)
                if (!self.isNative) {
                  saveAs(
                    out,
                    `${self.$t("custom.prolong_short_low")}_` +
                    date +
                    "_" +
                    booking.id +
                    ".docx"
                  );
                } else {
                  if (Filesystem.checkPermissions()) {
                    const getBase64FromBlob = async (blob) => {
                      return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.readAsDataURL(blob);
                        reader.onloadend = () => {
                          const base64data = reader.result;
                          resolve(base64data);
                        }
                      });
                    }
                    getBase64FromBlob(out).then(
                      (base64) => {
                        // save file
                        async function writeFile() {
                          return await Filesystem.writeFile({
                            path: `${self.$t("custom.prolong_short_low")}_` + date + "_" + booking.id + ".docx",
                            data: base64,
                            directory: Directory.Documents,
                          });
                        };
                        let file_uri = null
                        let writedFile = writeFile().then((getUriResult) => {
                          // share file opened file
                          let shareFile = async () => {
                            await Share.share({
                              title: 'Download document',
                              files: [getUriResult.uri],
                            }).catch((error) => {
                              console.log("shareFile", error)
                            });
                          };
                          let sharedFile = shareFile();
                        }).catch((error) => {
                          console.log("shareFile", error)
                        });
                      }
                    );
                  } else {
                    Filesystem.requestPermissions();
                  }
                }
              })
              .catch(function (error) {
                console.log(error);
                this.$swal({
                  toast: true,
                  position: "top-end",
                  showConfirmButton: false,
                  timer: 3000,
                  icon: "error",
                  title: this.$t("errors.gen_template_title"),
                  text: this.$t("errors.gen_template_text"),
                });
              });
          })
          .catch((error) => {
            this.setError(error, this.$t("errors.upload_template"));
            console.log(error);
          })
          .finally(() => (this.isLoadingGenerateProlongDoc = false));
      } else {
        this.$notify(this.$t("errors.no_access"), "", "warning");
      }
    },
    translateCountGroup(group) {
      this.$emit('translate-count-group', group);
      // Предполагаем, что родительский компонент обрабатывает этот метод
      // или можно перенести логику сюда, если она доступна
    },
    formatCreatedAt(date) {
      this.$emit('format-created-at', date);
      // Предполагаем, что родительский компонент обрабатывает этот метод
      // или можно перенести логику сюда, если она доступна
    },
    copyToBuffer(text) {
      navigator.clipboard.writeText(text);
      this.$swal({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        icon: "success",
        title: this.$t("custom.copied_to_buffer")
      });
    },
    setError(error, text) {
      this.$rollbar.configure({
        payload: {
          person: {
            id: this.$store.getters.getCurrentUser.id,
            username: this.$store.getters.getCurrentUser.name,
            email: this.$store.getters.getCurrentUser.email,
          },
          custom: {
            company: this.$store.getters.getCompany.company.name,
          },
        },
      });
      this.$rollbar.error(text, error);
      this.isLoading = false;
      this.$notify(this.$t("errors.error"), `${error}. ${text}`, "error", 5000);
      console.log(error);
      this.error =
        (error.response && error.response.data && error.response.data.error) ||
        text;
    },
  }
}
</script>

<style scoped>
.payment {
  flex-wrap: wrap;
}
</style>
